// The KPI TREND CHART (prototype screens.js metricChart, ported 1:1): the recorded value line + area over
// the charted window, the dashed human TARGET line when one is set, and the linked-Task markers — a ring on
// the value point at the week a Task landed, a dotted drop to its lettered badge. Every point is a RECORDED
// authoritative Value from the projection and every marker a real linked Task (P-SELF — nothing here is
// interpolated or judged; the trend JUDGMENT stays direction-only elsewhere, G-106). All colours/strokes
// come from the ported stylesheet classes (.mc-chart .grid/.target/.area/.val/.pt/.task-*/.badge-*/.ax —
// G-181); the geometry constants are the prototype's own viewBox numbers (unitless SVG coordinates).

import type { MetricPoint } from '../projections.ts';
import { fmtMetricValue, fmtDayMonth } from './metric-format.ts';

/** One lettered Task marker: the window-relative series index it pins to + its badge letter. */
export interface ChartMarker {
  readonly index: number;
  readonly letter: string;
}

/* The prototype's chart geometry (SVG viewBox units, not CSS px). */
const W = 1000;
const H = 380;
const PL = 64;
const PR = 28;
const PT = 26;
const PB = 78;
const TICKS = 4;
const BADGE_Y = H - 30;

interface Geometry {
  readonly lo: number;
  readonly hi: number;
  readonly px: (index: number) => number;
  readonly py: (value: number) => number;
}

/** The prototype's value→coordinate mapping: padded lo/hi (target included) onto the plot area. */
function buildGeometry(values: readonly number[], target: number | null): Geometry {
  let lo = Math.min(...values);
  let hi = Math.max(...values);
  if (target !== null) {
    lo = Math.min(lo, target);
    hi = Math.max(hi, target);
  }
  const pad = (hi - lo) * 0.22 || 1;
  lo -= pad;
  hi += pad;
  return {
    lo,
    hi,
    px: (index) => PL + (values.length <= 1 ? 0 : (index / (values.length - 1)) * (W - PL - PR)),
    py: (value) => PT + (1 - (value - lo) / (hi - lo)) * (H - PT - PB),
  };
}

/** The horizontal grid + y-axis tick labels. */
function GridLayer({ geo }: { readonly geo: Geometry }): React.JSX.Element {
  const rows = Array.from({ length: TICKS + 1 }, (_, k) => {
    const value = geo.lo + (k / TICKS) * (geo.hi - geo.lo);
    return { y: geo.py(value), label: fmtMetricValue(value) };
  });
  return (
    <>
      {rows.map((row) => (
        <g key={row.y.toFixed(1)}>
          <line className="grid" x1={PL} y1={row.y.toFixed(1)} x2={W - PR} y2={row.y.toFixed(1)} />
          <text className="ax yax" x={PL - 12} y={(row.y + 5).toFixed(1)}>
            {row.label}
          </text>
        </g>
      ))}
    </>
  );
}

/** The dashed human-target line + its label (only when a target is set — no hidden default). */
function TargetLayer({
  geo,
  target,
}: {
  readonly geo: Geometry;
  readonly target: number | null;
}): React.JSX.Element | null {
  if (target === null) return null;
  const y = geo.py(target);
  return (
    <>
      <line className="target" x1={PL} y1={y.toFixed(1)} x2={W - PR} y2={y.toFixed(1)} />
      <text className="ax tgt" x={PL + 4} y={(y - 9).toFixed(1)}>
        TARGET {fmtMetricValue(target)}
      </text>
    </>
  );
}

/** The linked-Task markers: value-point ring, dotted drop, lettered badge (prototype anatomy). */
function MarkerLayer({
  geo,
  values,
  markers,
}: {
  readonly geo: Geometry;
  readonly values: readonly number[];
  readonly markers: readonly ChartMarker[];
}): React.JSX.Element {
  return (
    <>
      {markers.map((marker) => {
        const x = geo.px(marker.index);
        const y = geo.py(values[marker.index] ?? geo.lo);
        return (
          <g key={marker.letter} data-chart-task={marker.letter}>
            <line
              className="task-conn"
              x1={x.toFixed(1)}
              y1={(y + 9).toFixed(1)}
              x2={x.toFixed(1)}
              y2={BADGE_Y - 12}
            />
            <circle className="task-ring" cx={x.toFixed(1)} cy={y.toFixed(1)} r={7} />
            <circle className="badge-c" cx={x.toFixed(1)} cy={BADGE_Y} r={12} />
            <text className="badge-t" x={x.toFixed(1)} y={BADGE_Y + 5}>
              {marker.letter}
            </text>
          </g>
        );
      })}
    </>
  );
}

/** The x-axis date labels: first / middle / last period end (prototype tick choice). */
function XAxisLayer({
  geo,
  series,
}: {
  readonly geo: Geometry;
  readonly series: readonly MetricPoint[];
}): React.JSX.Element {
  const count = series.length;
  const indexes = count <= 2 ? [0, count - 1] : [0, Math.floor((count - 1) / 2), count - 1];
  return (
    <>
      {[...new Set(indexes)].map((index) => (
        <text
          key={index}
          className="ax"
          x={geo.px(index).toFixed(1)}
          y={H - 8}
          style={{ textAnchor: index === 0 ? 'start' : index === count - 1 ? 'end' : 'middle' }}
        >
          {fmtDayMonth(series[index]!.periodEnd)}
        </text>
      ))}
    </>
  );
}

/** The full trend chart over the charted window (call with the windowed series). */
export function MetricChart({
  name,
  series,
  target,
  markers,
}: {
  readonly name: string;
  readonly series: readonly MetricPoint[];
  readonly target: number | null;
  readonly markers: readonly ChartMarker[];
}): React.JSX.Element {
  const values = series.map((point) => point.value);
  const geo = buildGeometry(values, target);
  const points = values.map((value, index) => [geo.px(index), geo.py(value)] as const);
  const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${geo.px(0).toFixed(1)},${H - PB} ${line} ${geo.px(values.length - 1).toFixed(1)},${H - PB}`;
  return (
    <svg
      className="mc-chart"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`${name} trend`}
    >
      <GridLayer geo={geo} />
      <TargetLayer geo={geo} target={target} />
      <polygon className="area" points={area} />
      <polyline className="val" points={line} />
      <MarkerLayer geo={geo} values={values} markers={markers} />
      {points.map(([x, y], index) => (
        <circle
          key={x.toFixed(1)}
          className={index === values.length - 1 ? 'pt pt-last' : 'pt'}
          cx={x.toFixed(1)}
          cy={y.toFixed(1)}
          r={index === values.length - 1 ? 5 : 3.4}
        />
      ))}
      <XAxisLayer geo={geo} series={series} />
    </svg>
  );
}
